/** @type {Map<string, Promise<string>>} */
const sectionCache = new Map();
const CACHE_MAX_SIZE = 100;

class CombinedOptions extends HTMLElement {
  #abortController;

  get buttons() {
    return this.querySelectorAll('button[data-product-url]');
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  connectedCallback() {
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.#handleClick);
    });
    this.#prefetchAll();
   
  }

  disconnectedCallback() {
    this.buttons.forEach((button) => {
      button.removeEventListener('click', this.#handleClick);
    });
  }

  #getSelectedOptionNames() {
    const section = this.closest('.shopify-section');
    if (!section) return {};
    const selected = {};
    section.querySelectorAll('variant-picker input[type="radio"]:checked').forEach((input) => {
      selected[input.name] = input.getAttribute('aria-label');
    });
    return selected;
  }

  #buildRequestUrl(button) {
    const productUrl = button.dataset.productUrl;
    const optionValues = JSON.parse(button.dataset.optionValues || '{}');
    const selectedOptions = this.#getSelectedOptionNames();
    const optionValueIds = Object.values(selectedOptions)
      .map((name) => optionValues[name])
      .filter(Boolean);

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    if (optionValueIds.length) {
      url.searchParams.set('option_values', optionValueIds.join(','));
    }
    return url.toString();
  }

  #handleClick = (event) => {
    const button = event.currentTarget;
    const productUrl = button.dataset.productUrl;
    if (new URL(productUrl, window.location.origin).pathname === window.location.pathname) return;

    this.#fetchAndReplace(productUrl, this.#buildRequestUrl(button));
  };

  #prefetchAll() {
    this.buttons.forEach((button) => {
      const productUrl = button.dataset.productUrl;
      if (new URL(productUrl, window.location.origin).pathname === window.location.pathname) return;

      const urlString = this.#buildRequestUrl(button);

      if (!sectionCache.has(urlString)) {
        const promise = fetch(urlString, { priority: 'low' })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .catch((error) => {
            sectionCache.delete(urlString);
            throw error;
          });

        sectionCache.set(urlString, promise);

        if (sectionCache.size > CACHE_MAX_SIZE) {
          sectionCache.delete(sectionCache.keys().next().value);
        }
      }
    });
   
  }

  async #fetchAndReplace(productUrl, requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    const cached = sectionCache.get(requestUrl);

    try {
      const html = cached
        ? await cached
        : await fetch(requestUrl, { signal }).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          });

      if (signal.aborted) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newSection = parsed.querySelector('.shopify-section');

      if (!newSection) return;

      const currentSection = this.closest('.shopify-section');
      if (!currentSection) return;

      const transition = () => {
        currentSection.innerHTML = newSection.innerHTML;
        if (Theme.template.name === 'product') {
          history.replaceState({}, '', productUrl);
        }
        if (typeof yotpoWidgetsContainer !== 'undefined') {
          yotpoWidgetsContainer.initWidgets();
        }
       
      };

      if (document.startViewTransition) {
        document.startViewTransition(transition);
      } else {
        transition();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
    }
  }
}

if (!customElements.get('combined-options')) {
  customElements.define('combined-options', CombinedOptions);
}
